import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ViolationEventsTab from './components/ViolationEventsTab';
import ViolationRulesTab from './components/ViolationRulesTab';
import ViolationRuleFormDrawer from './components/ViolationRuleFormDrawer';
import AnalyticsTab from './analytics/AnalyticsTab';
import ChangeRequestsTab from './analytics/ChangeRequestsTab';
import MyChangeRequestsTab from './analytics/MyChangeRequestsTab';
import { isAdmin } from '../../services/api';
import { useNavigationEntitlements } from '../../hooks/useNavigationEntitlements';
import { isNavigationKeyAllowed, NAV_KEYS } from '../../utils/navigationEntitlements';
import { useChangeRequestSummary } from '../../hooks/useChangeRequestSummary';
import { useTabFilterCache } from '../../hooks/useTabFilterCache';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';
import './ViolationsPage.css';

/*
 * Раздел «Нарушения», Part 1.
 * Спека: docs/superpowers/specs/2026-09-02-violations-part1-design.md
 */

// «Аналитика камеры» открыта всем: по политике камеры клиент может предлагать
// зоны и правила заявкой (ТЗ 6.9, 7.1), а содержимое ограничивает capability.
// ТЗ 30–32: «Мои заявки» — зеркало «Заявок» администратора, но для клиента.
const ALL_TABS = ['events', 'rules', 'requests', 'my-requests', 'analytics'];

// ТЗ BM v2 §1: формы правила адресуемы — drawer=create, drawer=edit&rule_id=….
// Это состояние открытой формы, а не фильтр вкладки: при смене вкладки оно
// не запоминается и не переносится.
const DRAWER_PARAMS = ['drawer', 'rule_id'];
const withoutDrawer = (params) => {
  const next = new URLSearchParams(params);
  DRAWER_PARAMS.forEach((name) => next.delete(name));
  return next;
};

const ViolationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const admin = isAdmin();
  // ТЗ 27: тот же badge, что и в Sidebar — Sidebar и эта вкладка живут в
  // разных деревьях компонентов, поэтому каждый вызывает хук отдельно;
  // оба подписаны на CHANGE_REQUESTS_REFRESH и обновятся синхронно.
  const { pendingCount } = useChangeRequestSummary({ enabled: admin });
  const [rulesRefreshToken, setRulesRefreshToken] = useState(0);
  // ТЗ 35 (дополнение Task 14): открытая форма правила должна перечитать
  // каталог метрик/зон, если зоны камеры поменялись, пока форма открыта.
  const [zonesVersion, setZonesVersion] = useState(0);
  const [pageNotice, setPageNotice] = useState('');
  const { keys: navigationKeys, status: navigationStatus } = useNavigationEntitlements();
  const navigationLoading = navigationStatus === 'loading';
  const requestedTab = searchParams.get('tab');
  const ruleIdParam = searchParams.get('ruleId') || '';
  const drawerParam = searchParams.get('drawer') || '';
  const editRuleIdParam = drawerParam === 'edit' ? (searchParams.get('rule_id') || '') : '';
  // Свежие параметры для обработчиков, вызванных из async-колбэков дочерних
  // компонентов: замкнутый searchParams там уже устарел бы.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // Вкладки по ключам навигации v2. Неизвестный ключ вкладку не создаёт.
  // «Правила» — ключ events.rules, по умолчанию выключен: обычному пользователю
  // вкладка открывается только явно выданным ключом events.rules. «Заявки» (модерация) —
  // только администратору, «Мои заявки» — только пользователю.
  const visibleTabs = useMemo(() => {
    const allowed = (key) => isNavigationKeyAllowed(navigationKeys, key);
    return ALL_TABS.filter((id) => {
      if (id === 'events') return allowed(NAV_KEYS.EVENTS_LIST);
      if (id === 'rules') {
        return allowed(NAV_KEYS.EVENTS_RULES)
          && (admin || (Array.isArray(navigationKeys) && navigationKeys.includes(NAV_KEYS.EVENTS_RULES)));
      }
      if (id === 'requests') return admin && allowed(NAV_KEYS.EVENTS_CHANGE_REQUESTS);
      if (id === 'my-requests') return !admin && allowed(NAV_KEYS.EVENTS_CHANGE_REQUESTS);
      return allowed(NAV_KEYS.EVENTS_CAMERA_SETTINGS);
    });
  }, [navigationKeys, admin]);

  const activeTab = useMemo(() => {
    if (visibleTabs.includes(requestedTab)) return requestedTab;
    return visibleTabs[0] || null;
  }, [requestedTab, visibleTabs]);

  // Административные вкладки закрыты для user и наоборот — «Мои заявки»
  // относится к пользователю без прав администратора. В обоих направлениях
  // чистим tab-параметр, чтобы URL не врал. Окончательное решение по правам
  // всё равно остаётся за API.
  useEffect(() => {
    // Пока ключи грузятся, видимость вкладок неизвестна — tab из URL не трогаем.
    if (navigationLoading) return;
    const tabIsForbidden =
      ALL_TABS.includes(requestedTab) && !visibleTabs.includes(requestedTab);
    if (tabIsForbidden) {
      const params = new URLSearchParams(searchParams);
      params.delete('tab');
      setSearchParams(params, { replace: true });
    }
  }, [requestedTab, admin, visibleTabs, navigationLoading, searchParams, setSearchParams]);

  const isRuleFormOpen = activeTab === 'rules' && drawerParam === 'create';

  const { saveTabParams, restoreTabParams } = useTabFilterCache();

  // FE-FIX08: success-уведомление гаснет само, ошибки — нет.
  useAutoDismiss(pageNotice, setPageNotice);

  const handleTabClick = (tabId) => {
    if (tabId === activeTab) return;
    // FE-FILTER02: у каждой вкладки свои фильтры в URL — старые не должны
    // утечь в новую вкладку. FE-FIX02: но и выбрасывать их нельзя — фильтры
    // уходящей вкладки запоминаем и подставляем обратно при возврате.
    saveTabParams(activeTab, withoutDrawer(searchParams));
    const next = withoutDrawer(restoreTabParams(tabId));
    if (tabId !== 'events') next.set('tab', tabId);
    setSearchParams(next);
    setPageNotice('');
  };

  const handleRuleIdChange = (ruleId) => {
    const params = new URLSearchParams(searchParamsRef.current);
    if (ruleId) params.set('ruleId', ruleId);
    else params.delete('ruleId');
    setSearchParams(params, { replace: true });
  };

  // Форма изменения и карточка правила меняются одним шагом. Открытие формы —
  // отдельный шаг истории (Back закрывает её), закрытие — replace.
  const handleEditRuleChange = (editRuleId, viewRuleId) => {
    const params = withoutDrawer(searchParamsRef.current);
    if (editRuleId) {
      params.set('drawer', 'edit');
      params.set('rule_id', editRuleId);
    }
    if (viewRuleId) params.set('ruleId', viewRuleId);
    else params.delete('ruleId');
    setSearchParams(params, { replace: !editRuleId });
  };

  const openCreateForm = () => {
    const params = withoutDrawer(searchParamsRef.current);
    params.set('drawer', 'create');
    setSearchParams(params);
  };

  const closeCreateForm = () => {
    setSearchParams(withoutDrawer(searchParamsRef.current), { replace: true });
  };

  return (
    <div className="vio-container">
      <h1>События</h1>

      <div className="vio-tabs">
        {visibleTabs.includes('events') && (
          <button
            type="button"
            className={`vio-tab-btn${activeTab === 'events' ? ' vio-tab-btn--active' : ''}`}
            onClick={() => handleTabClick('events')}
          >
            {/* FE-FIX14: раздел уже называется «События» — вкладка «Список»,
                иначе получалось «События → События». */}
            Список
          </button>
        )}
        {visibleTabs.includes('rules') && (
          <button
            type="button"
            className={`vio-tab-btn${activeTab === 'rules' ? ' vio-tab-btn--active' : ''}`}
            onClick={() => handleTabClick('rules')}
          >
            Правила
          </button>
        )}
        {visibleTabs.includes('requests') && (
          <button
            type="button"
            className={`vio-tab-btn${activeTab === 'requests' ? ' vio-tab-btn--active' : ''}`}
            onClick={() => handleTabClick('requests')}
          >
            Заявки
            {pendingCount > 0 && <span className="vio-tab-badge">{pendingCount}</span>}
          </button>
        )}
        {visibleTabs.includes('my-requests') && (
          <button
            type="button"
            className={`vio-tab-btn${activeTab === 'my-requests' ? ' vio-tab-btn--active' : ''}`}
            onClick={() => handleTabClick('my-requests')}
          >
            Мои заявки
          </button>
        )}
        {visibleTabs.includes('analytics') && (
          <button
            type="button"
            className={`vio-tab-btn${activeTab === 'analytics' ? ' vio-tab-btn--active' : ''}`}
            onClick={() => handleTabClick('analytics')}
          >
            Настройки камер
          </button>
        )}
      </div>

      {pageNotice && (
        <div className="vio-card">
          <p className="vio-notice">{pageNotice}</p>
        </div>
      )}

      {navigationLoading && <div className="vio-card"><p className="vio-notice">Загрузка…</p></div>}
      {!navigationLoading && !activeTab && (
        <div className="vio-card"><p className="vio-notice">Нет доступных разделов событий</p></div>
      )}

      {!navigationLoading && activeTab === 'events' && <ViolationEventsTab />}
      {!navigationLoading && activeTab === 'rules' && (
        <ViolationRulesTab
          onCreateRule={openCreateForm}
          refreshToken={rulesRefreshToken}
          initialRuleId={ruleIdParam}
          onRuleIdChange={handleRuleIdChange}
          editRuleId={editRuleIdParam}
          onEditRuleChange={handleEditRuleChange}
          catalogRefreshToken={zonesVersion}
        />
      )}
      {!navigationLoading && activeTab === 'requests' && (
        // Одобренная заявка меняет правила камеры — список правил после этого
        // перечитывается, а не остаётся с прежним состоянием.
        <ChangeRequestsTab onApplied={() => setRulesRefreshToken((token) => token + 1)} />
      )}
      {!navigationLoading && activeTab === 'my-requests' && <MyChangeRequestsTab />}
      {!navigationLoading && activeTab === 'analytics' && (
        // Каскадное выключение детекции деактивирует правила: список правил
        // после этого обязан перечитаться, а не остаться со старым состоянием.
        <AnalyticsTab
          onRulesChanged={() => setRulesRefreshToken((token) => token + 1)}
          onZonesChanged={() => setZonesVersion((token) => token + 1)}
        />
      )}

      {isRuleFormOpen && (
        <ViolationRuleFormDrawer
          catalogRefreshToken={zonesVersion}
          onClose={closeCreateForm}
          onCreated={() => {
            closeCreateForm();
            setRulesRefreshToken((token) => token + 1);
            setPageNotice('Правило создано и пока неактивно — активируйте его отдельно, когда будете готовы.');
          }}
          // Правило создано, но ускорение или расписание нет: форма остаётся
          // открытой с ошибкой, а список правил уже должен показать новое правило.
          onPartiallySaved={() => setRulesRefreshToken((token) => token + 1)}
        />
      )}
    </div>
  );
};

export default ViolationsPage;
